<template lang="pug">
.add-popover(
  v-if="visible"
  :class="{ 'add-popover--above': openAbove, 'add-popover--left': openLeft }"
  :style="positionStyle"
  role="dialog"
  aria-labelledby="add-popover-title")
  .add-popover__pointer
  .add-popover__panel
    p#add-popover-title.add-popover__message
      | Open this session in TASB Events to add or remove from your schedule.
    .add-popover__actions
      button.add-popover__btn-primary(type="button" @click="onOpen") Open
      button.add-popover__btn-secondary(type="button" @click="onCancel") Cancel
</template>

<script>
const POPOVER_WIDTH = 296;
const POPOVER_OFFSET_Y = 12;

export default {
  name: 'AddSessionPopover',

  props: {
    visible: { type: Boolean, default: false },
    triggerEl: { type: [Object, Element], default: null },
    sessionUrl: { type: String, default: '' },
  },

  data() {
    return {
      top: 0,
      left: 0,
      openAbove: false,
      openLeft: false,
    };
  },

  computed: {
    positionStyle() {
      return {
        position: 'fixed',
        top: `${this.top}px`,
        left: `${this.left}px`,
        width: `${POPOVER_WIDTH}px`,
        zIndex: 11000,
      };
    },
  },

  watch: {
    visible(now) {
      if (now) this.attachListeners();
      else this.detachListeners();
    },
    triggerEl() {
      if (this.visible) this.$nextTick(() => this.computePosition());
    },
  },

  beforeDestroy() {
    this.detachListeners();
  },

  methods: {
    computePosition() {
      if (!this.triggerEl) return;
      const r = this.triggerEl.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const popW = POPOVER_WIDTH;
      // Read rendered height; fall back to 140 if element not yet in DOM
      const popH = (this.$el && this.$el.offsetHeight) || 140;

      // Vertical: prefer below; flip above when popover clips bottom and more room above
      const spaceBelow = vh - r.bottom - POPOVER_OFFSET_Y;
      const spaceAbove = r.top - POPOVER_OFFSET_Y;
      this.openAbove = spaceBelow < popH && spaceAbove >= popH;

      const top = this.openAbove
        ? r.top - POPOVER_OFFSET_Y - popH
        : r.bottom + POPOVER_OFFSET_Y;

      // Horizontal: right-align to trigger's right edge by default;
      // flip to left-align when right-aligned position clips the left viewport edge
      const rightAligned = r.right - popW;
      this.openLeft = rightAligned < 8;
      const rawLeft = this.openLeft ? r.left : rightAligned;

      this.top = top;
      this.left = Math.max(8, Math.min(vw - popW - 8, rawLeft));
    },

    attachListeners() {
      // $nextTick ensures the element is in the DOM so offsetHeight is readable
      this.$nextTick(() => this.computePosition());
      document.addEventListener('mousedown', this.onOutsideClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('scroll', this.computePosition, true);
      window.addEventListener('resize', this.close);
    },

    detachListeners() {
      document.removeEventListener('mousedown', this.onOutsideClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.computePosition, true);
      window.removeEventListener('resize', this.close);
    },

    onOutsideClick(e) {
      if (!this.$el || !this.$el.contains) return;
      if (this.$el === e.target || (this.$el.contains && this.$el.contains(e.target))) return;
      if (this.triggerEl && this.triggerEl.contains(e.target)) return;
      this.close();
    },

    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    },

    onOpen() {
      if (this.sessionUrl) window.open(this.sessionUrl, '_blank', 'noopener');
      this.close();
    },

    onCancel() { this.close(); },

    close() { this.$emit('close'); },
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.add-popover {
  pointer-events: auto;
  font-family: 'Public Sans', sans-serif;
  display: flex;
  flex-direction: column;
}

.add-popover__pointer {
  width: 0;
  height: 0;
  margin-left: auto;
  margin-right: 16px;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid white;
  filter: drop-shadow(0 -1px 1px rgba(22, 56, 116, 0.08));
}

// Placement: popover opens above trigger — flip arrow to bottom, pointing down
.add-popover--above {
  .add-popover__pointer {
    order: 1;
    border-bottom: none;
    border-top: 10px solid white;
    filter: drop-shadow(0 1px 1px rgba(22, 56, 116, 0.08));
  }

  .add-popover__panel {
    order: 0;
  }
}

// Placement: popover left-aligns to trigger — shift arrow to left
.add-popover--left {
  .add-popover__pointer {
    margin-left: 16px;
    margin-right: auto;
  }
}

.add-popover__panel {
  background: white;
  border: 1px solid $color-border;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 16px 24px -6px rgba(22, 56, 116, 0.14), 0 8px 10px -6px rgba(22, 56, 116, 0.1);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.add-popover__message {
  margin: 0;
  font-size: 16px;
  color: $color-text-secondary;
  line-height: 1.5;
}

.add-popover__actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.add-popover__btn-primary,
.add-popover__btn-secondary {
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.add-popover__btn-primary {
  background: $color-primary;
  color: white;
  border: none;
}

.add-popover__btn-secondary {
  background: white;
  color: $color-text-dark;
  border: 1px solid $color-primary;
  box-shadow: $shadow-btn;

  &:hover {
    background: $color-btn-outline-hover-bg;
    border-color: $color-btn-outline-hover-border;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}
</style>
